/**
 * Undo Store - Manages undo history for editor actions
 *
 * Tracks actions (add, delete, edit) and allows undoing them
 */

import { writable, derived } from 'svelte/store';

export type UndoTarget = 'wordbank' | 'prompt' | 'framework';
export type UndoType = 'add' | 'delete' | 'edit';

export interface UndoAction {
  type: UndoType;
  target: UndoTarget;
  targetKey: string; // Which wordbank/category
  itemKey?: string; // Which word/prompt/framework (for identification)
  previousValue: any; // Complete state before action
  newValue: any; // Complete state after action
  timestamp: number;
}

const MAX_HISTORY = 50;

function createUndoStore() {
  const { subscribe, set, update } = writable<UndoAction[]>([]);

  return {
    subscribe,

    /**
     * Record an action in the undo history
     */
    recordAction(action: Omit<UndoAction, 'timestamp'>) {
      update(history => {
        const newHistory = [
          ...history.slice(-MAX_HISTORY + 1),
          {
            ...action,
            timestamp: Date.now(),
          },
        ];
        return newHistory;
      });
    },

    /**
     * Undo the last action
     * Returns the action that was undone, or null if nothing to undo
     */
    undo(): UndoAction | null {
      let lastAction: UndoAction | null = null;

      update(history => {
        if (history.length === 0) {
          return history;
        }

        lastAction = history[history.length - 1];
        return history.slice(0, -1);
      });

      return lastAction;
    },

    /**
     * Clear all undo history
     */
    clearHistory() {
      set([]);
    },

    /**
     * Get the last action without removing it
     */
    peekLast(state: UndoAction[]): UndoAction | null {
      return state.length > 0 ? state[state.length - 1] : null;
    },
  };
}

export const undoStore = createUndoStore();

/**
 * Derived store to check if undo is available
 */
export const canUndo = derived(undoStore, $history => $history.length > 0);
